import { FC } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ModernClientReservationCard from "components/ClientReservationCard/ModernClientReservationCard";
import Pagination from "shared/Pagination/Pagination";
import { CalendarDaysIcon, MapPinIcon, StarIcon } from "@heroicons/react/24/outline";

interface ModernClientEventsGridProps {
  loading: boolean;
  reservations: any[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  selectedFilter?: string;
  filterStatus?: string | null;
}

const ModernClientEventsGrid: FC<ModernClientEventsGridProps> = ({
  loading,
  reservations,
  totalCount,
  totalPages,
  currentPage,
  onPageChange,
  selectedFilter = "all",
  filterStatus = null,
}) => {
  const navigate = useNavigate();

  // Filter reservations based on selected filter
  const filteredReservations =
    selectedFilter === "all" || !filterStatus
      ? reservations
      : reservations.filter((reservation: any) => reservation.status === filterStatus);

  const filteredTotalCount = filteredReservations.length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.95,
    },
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center mb-6">
        <CalendarDaysIcon className="w-16 h-16 text-blue-500 dark:text-blue-400" />
      </div>

      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Aucun événement pour le moment</h3>

      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-8 leading-relaxed">
        Commencez à organiser vos événements en réservant des espaces uniques pour vos occasions spéciales.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
        {[
          { icon: MapPinIcon, title: "Explorez", desc: "Des espaces uniques" },
          { icon: CalendarDaysIcon, title: "Planifiez", desc: "Votre événement parfait" },
          { icon: StarIcon, title: "Profitez", desc: "De moments inoubliables" },
        ].map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
              className="text-center p-4"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{step.title}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{step.desc}</p>
            </motion.div>
          );
        })}
      </div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="mt-8 px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
        onClick={() => navigate("/espace-evenementiel")}
      >
        Explorer les espaces
      </motion.button>
    </motion.div>
  );

  const LoadingState = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="relative mb-6">
        <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-purple-400 dark:border-r-purple-500 rounded-full animate-spin animation-delay-75"></div>
      </div>

      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Chargement de vos événements</h3>

      <p className="text-gray-600 dark:text-gray-400">Veuillez patienter quelques instants...</p>
    </motion.div>
  );

  if (loading) {
    return <LoadingState />;
  }

  if (filteredTotalCount === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6 mt-8">
      {/* Events Grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
        <AnimatePresence mode="wait">
          {filteredReservations.map((item, index) => (
            <motion.div
              key={`${item.id}-${currentPage}`}
              variants={cardVariants}
              transition={{ duration: 0.5, ease: "easeOut" }}
              layout
              className="relative mx-4"
            >
              <ModernClientReservationCard defaultOpen={index === 0} data={item} totalCount={filteredTotalCount} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Modern Pagination */}
      {filteredTotalCount > 8 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center mt-12"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ModernClientEventsGrid;
